import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Button, Card, Col, Container, Dropdown, DropdownButton, Form, Row} from "react-bootstrap";
import { PencilIcon } from '@primer/octicons-react'
import { axiosAuth_DATA_MGMT_API } from '../utils/AxoisUtilComponent';
import { useTranslation } from 'react-i18next';
import classes from './AdminPages.module.css';
import { translateAPIError } from '../utils/apiUtils';
import { testJSON } from '../utils/jsUtils';
import { formatDate } from '../utils/timeUtils';


const ModelAdminDetailsView = (props) => {

  // Utilities
  const { t } = useTranslation(['common', 'custom']);
  const navigate = useNavigate();

  // Initialize with actual JSON obj.  If not, will get error.
  const [modelAttrs, setModelAttrs] = useState({
    id: '',
    name: '',
    description: '',
    version: '',
    config: '',
    status: ''
  });

  const [currentAttrs, setCurrentAttrs] = useState(null);

  const statusOptions = [
    { key: "ST_ENABLED",  value: "ST_ENABLED" },
    { key: "ST_DISABLED", value: "ST_DISABLED" }
  ];


  // View vs Edit Modes
  const [editMode, setEditMode] = useState(false);

  const [errMsg,     setErrMsg]     = useState('');
  const [errMsgEdit, setErrMsgEdit] = useState('');


  // UI Functions

  // Initialize state
  useEffect(() => {
    if (props) {
      setModelAttrsWithFormatting (props.objDetails);
    }
  }, [props]);


  const setModelAttrsWithFormatting = (modelDetails) => {

    // Properly format receiver settings JSON
    // Makes a deep copy
    let inputDetails = JSON.parse (JSON.stringify (modelDetails));

    inputDetails.config = JSON.stringify(JSON.parse(JSON.stringify(modelDetails.config)), undefined, 4);

    setModelAttrs(inputDetails);
    setCurrentAttrs(inputDetails);
  }

  const onStatusSelect = (key, event) => {
    setModelAttrs({ ...modelAttrs, status: key });
  };

  const handleEditAttrs = (e) => {
    e.preventDefault();
    setEditMode(true);
    setErrMsg('');
  }


  const onAttrsInputChange = (e) => {
    setModelAttrs({ ...modelAttrs, [e.target.name]: e.target.value })
  }

  const handleEditCancel = (e) => {
    e.preventDefault();

    // Reset fields
    setModelAttrs (currentAttrs);

    setEditMode(false);
    setErrMsgEdit('');
  }

  const handleEditSubmit = (e) => {
    e.preventDefault();

    if (!modelAttrs.name || modelAttrs.name.trim().length === 0) {
      setErrMsgEdit(t("I18N_ERR_Need_name"));
    } else if (modelAttrs.name.length > 50) {
      setErrMsgEdit(t("I18N_ERR_Max_len_name") + "50");
    } else if (modelAttrs.name !== currentAttrs.name) {
      validateName();
    } else {
      validateOtherFields();
    }

    return;
  }

  // Since this is async, need to call this first, and check the others when username availability returns
  function validateName() {

    new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_DATA_MGMT_API.get("3d-models?name=" + modelAttrs.name);

        resolve(res.data);

        if ((res.status === 200) && (res.data.length > 0)) {
          // Model name is taken
          setErrMsgEdit(t("I18N_ERR_Name_taken"));
          return;
        } else {
          validateOtherFields();
          return;
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsgEdit(t(translateAPIError(error)));
          validateOtherFields();
        }
        return;
      }
    });
  }

  function validateOtherFields() {
    // Validate form inputs
    if (!modelAttrs.description || modelAttrs.description.trim().length === 0) {
      setErrMsgEdit(t("I18N_ERR_Need_description"));
    } else if (modelAttrs.description.length > 200) {
      setErrMsgEdit(t("I18N_ERR_Max_len_desc") + "200");

    } else if (!modelAttrs.version || modelAttrs.version.trim().length === 0) {
      setErrMsgEdit(t("I18N_ERR_Need_version"));
    } else if (modelAttrs.version.length > 20) {
      setErrMsgEdit(t("I18N_ERR_Max_len_version") + "20");

    } else if (!modelAttrs.status || modelAttrs.status.length === 0) {
      setErrMsgEdit(t("I18N_ERR_Need_status"));

    } else if (!modelAttrs.config || modelAttrs.config.trim().length === 0) {
      setErrMsgEdit(t("I18N_ERR_Need_config"));
    } else if (   (modelAttrs.config !== '')
               && (!testJSON(modelAttrs.config))) {
      // Validate configuration to be valid JSON
      setErrMsgEdit(t("I18N_ERR_Config_need_valid_JSON"));
    } else {
      updateModel();
    }
  }

  const updateModel = () => {

    // Makes a deep copy
    const reqBody = JSON.parse (JSON.stringify (modelAttrs));
    reqBody.config = JSON.parse(modelAttrs.config);

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_DATA_MGMT_API.put("3d-models/" + modelAttrs.id, reqBody);

        resolve(res.data);

        if (res.status === 200) {
          setCurrentAttrs(modelAttrs);

          // Restore UI to viewing mode
          setEditMode(false);
          setErrMsgEdit('');
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsgEdit(t(translateAPIError(error)));
        }
      }
    });

  }


  const handleClose = (e) => {
    e.preventDefault();

    // Return to list
    navigate(-1);
  }


  return (
    <>
      <Card style={{overflow:'auto', border:'0', boxShadow:'0 2px 2px 0px rgba(0,0,0,0.1)',
                    padding:'20px 40px 20px 40px', maxWidth:'2500px', width:'100%', fontSize:'13px'}}>

        <div style={{display:'flex'}}>
        <span className={classes.admin_pages__page_title}>
          {t("I18N_3dModel_Details")}
        </span>
        </div>

        <div className={classes.admin_pages__page_subtitle} style={{marginTop:'20px'}}>
          <div display='flex'>
            <span>
              {t("I18N_3dModel_info")}
            </span>
            {editMode ?
              null
              :
              <span style={{marginLeft:'15px', fontSize:'9px', color:'SteelBlue', cursor:'pointer'}} onClick={handleEditAttrs}>
                <PencilIcon size={12} />
                {t("I18N_Edit")}
              </span>
            }
          </div>
          <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

          {editMode ?
            <Container>
              <form>
                <Row className={classes.admin_pages__field_label}>
                  <Col md={3}>{t("I18N_Name")}: *</Col>
                  <Col md={3}>{t("I18N_Description")}:</Col>
                  <Col md={3}>{t("I18N_Version")}: *</Col>
                  <Col md={3}>{t("I18N_Status")}: *</Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col md={3}>
                    <input className={classes.admin_pages__field_editable} name="name" value={modelAttrs.name}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col md={3}>
                    <textarea className={classes.admin_pages__field_editable} style={{width:'100%'}} name="description" value={modelAttrs.description}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col md={3}>
                    <input className={classes.admin_pages__field_editable} name="version" value={modelAttrs.version}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col md={3}>
                    <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                      title={!modelAttrs.status ? t("common:I18N_DROPDOWN_PROMPT") :
                              t("I18N_" + (statusOptions.find( ({ key }) => key === modelAttrs.status )).value)}
                      onSelect={onStatusSelect}>
                      {statusOptions.map((item, index) => {
                        return (
                          <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                            {t("I18N_" + item.value)}
                          </Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
                  </Col>
                </Row>

                <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                  <Col sm={{span: 12}}>{t("I18N_3d_model_config")}: *</Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col sm={{span: 12}}>
                    <textarea className={classes.admin_pages__field_editable} rows={10} style={{minWidth: '100%', minHeight:'400px'}} name="config"
                              value={modelAttrs.config} onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                </Row>

                <Row className={classes.admin_pages__field_label} style={{marginTop:'10px', fontStyle:'italic'}}>
                  <span>{t("I18N_Req_fields")}</span>
                </Row>

                <Row>
                  <div display='flex' style={{marginTop:'15px'}}>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ float: 'left' }}
                      onClick={handleEditCancel}>
                      {t("I18N_Cancel")}
                    </Button>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ marginLeft: '15px' }}
                      onClick={handleEditSubmit}>
                      {t("I18N_Save")}
                    </Button>
                    <span className={classes.admin_pages__field_label} style={{marginLeft:'30px', color:'rgba(200,0,0)', verticalAlign:'top'}}>
                      {errMsgEdit}
                    </span>
                  </div>
                </Row>
              </form>
            </Container>
            :
            <Container>
              <Row className={classes.admin_pages__field_label}>
                <Col md={3}>{t("I18N_Name")}:</Col>
                <Col md={3}>{t("I18N_Description")}:</Col>
                <Col md={3}>{t("I18N_Version")}:</Col>
                <Col md={3}>{t("I18N_Status")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col md={3}>{modelAttrs.name}</Col>
                <Col style={{maxHeight:'80px', overflow:'auto', flexWrap:'wrap', wordWrap:'break-word'}}>
                  {modelAttrs.description}
                </Col>
                <Col md={3}>{modelAttrs.version}</Col>
                <Col md={3}>{t("I18N_" + modelAttrs.status)}</Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col sm={{span: 12}}>{t("I18N_3d_model_config")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col sm={{span: 12}}>
                  <textarea className={classes.admin_pages__field_editable} rows={10} style={{minWidth: '100%', minHeight:'400px', readonly:'readonly' }}
                            onChange={(e) => {}} value={modelAttrs.config} />
                </Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>{t("I18N_Created_by")}:</Col>
                <Col>{t("I18N_Time_created")}:</Col>
                <Col>{t("I18N_Updated_by")}:</Col>
                <Col>{t("I18N_Time_updated")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col style={{overflowWrap:'anywhere'}}>{modelAttrs.creatorUsername}</Col>
                <Col>{formatDate(modelAttrs.timeCreated)}</Col>
                <Col style={{overflowWrap:'anywhere'}}>{modelAttrs.updaterUsername}</Col>
                <Col>{formatDate(modelAttrs.timeUpdated)}</Col>
              </Row>

            </Container>
          }
        </div>


        <hr className="solid" style={{ marginTop: '30px' }}></hr>

        <div className={classes.admin_pages__page_subtitle} >
          <Form.Group as={Row} className="mb-2 mt-3">
            <div style={{ display: "flex" }}>
              <Button size="sm" variant="outline-secondary" className="mb-3"
                style={{ fontSize: '12px', fontWeight: '300' }}
                onClick={handleClose}>
                {t("I18N_Close")}
              </Button>
              <span className={classes.admin_pages__field_label} style={{marginLeft:'30px', color:'rgba(200,0,0)', verticalAlign:'top'}}>
                {errMsg}
              </span>
            </div>
{/*
              <span className={classes.admin_pages__field_label} style={{ color: 'rgba(200,0,0)', verticalAlign: 'top' }}>
                {updateModelErrMsg}
              </span>
*/}
          </Form.Group>
        </div>

      </Card>
    </>
  );
}
 
export default ModelAdminDetailsView;
